import {
  Banner,
  Box,
  Grid,
  ReadOnlyField,
  Typography,
} from '@oresundsbron/bridge-ui';
import {
  Notice,
  Typography as OBDTypography,
} from '@oresundsbron/design-system';
import { BusinessContactInformation } from '@oresundsbron/validators';
import { FC } from 'react';
import { useCountryName } from '../../../hooks/useCountryName';
import { useTranslation } from 'next-i18next';
import { ValidationResult } from '@oresundsbron/use-form';

export type Labels = BusinessContactInformation & { contactPerson: string };

interface ContactInformationProps {
  data: BusinessContactInformation;
  labels: Labels;
  title: string;
  disclaimer: string;
  isHidePhone: boolean;
  contactInfoValidation?: ValidationResult<string> | undefined;
}

const BusinessContactInformation: FC<ContactInformationProps> = ({
  data,
  labels,
  title,
  disclaimer,
  isHidePhone,
  contactInfoValidation,
}) => {
  const getCountryName = useCountryName();

  const { t } = useTranslation(['account', 'common'], {
    nsMode: 'fallback',
  });

  const renderNameValidation = (labelError: string | undefined) =>
    !!labelError ? (
      <Notice variant="error">
        <OBDTypography mb={0} pb={0} pt={0} mt={0} variant="small">
          {labelError}
        </OBDTypography>
      </Notice>
    ) : null;

  return (
    <Box className="w-full">
      <Typography as="h3" intent="headline" size="sm" gutter>
        {title}
      </Typography>
      <Banner color="inform">{disclaimer}</Banner>
      <Grid>
        <ReadOnlyField label={labels.vatno} className="col-span-full">
          {data.vatno || '-'}
        </ReadOnlyField>
        <ReadOnlyField label={labels.country} className="col-span-full">
          {getCountryName(data.country) ?? '-'}
        </ReadOnlyField>
        <ReadOnlyField
          label={labels.zipCode}
          className="col-span-4 md:col-span-6"
        >
          {data.zipCode || '-'}
        </ReadOnlyField>
        <ReadOnlyField label={labels.city} className="col-span-4 md:col-span-6">
          {data.city || '-'}
        </ReadOnlyField>
        <ReadOnlyField label={labels.address} className="col-span-full">
          {data.address || '-'}
        </ReadOnlyField>
        {!isHidePhone ? (
          <>
            <ReadOnlyField
              label={labels.phoneCode}
              className="col-span-4 md:col-span-6"
            >
              {data.phoneCode || '-'}
            </ReadOnlyField>
            <ReadOnlyField
              label={labels.phone}
              className="col-span-4 md:col-span-6"
            >
              {data.phone || '-'}
            </ReadOnlyField>
          </>
        ) : null}
        <Typography intent="title" className="col-span-full mt-4">
          {labels.contactPerson}
        </Typography>
        <Box className="col-span-4 md:col-span-6">
          <ReadOnlyField label={labels.contact.firstName}>
            {data.contact.firstName || '-'}
          </ReadOnlyField>
          {renderNameValidation(contactInfoValidation?.firstName as string)}
        </Box>
        <Box className="col-span-4 md:col-span-6">
          <ReadOnlyField label={labels.contact.lastName}>
            {data.contact.lastName || '-'}
          </ReadOnlyField>
          {renderNameValidation(contactInfoValidation?.lastName as string)}
        </Box>
        <ReadOnlyField label={labels.contact.email} className="col-span-full">
          {data.contact.email || '-'}
        </ReadOnlyField>
        {!isHidePhone ? (
          <>
            <ReadOnlyField
              label={labels.contact.phoneCode}
              className="col-span-4 md:col-span-6"
            >
              {data.contact.phoneCode || '-'}
            </ReadOnlyField>
            <ReadOnlyField
              label={labels.contact.phone}
              className="col-span-4 md:col-span-6"
            >
              {data.contact.phone || '-'}
            </ReadOnlyField>
          </>
        ) : null}
      </Grid>
    </Box>
  );
};

export default BusinessContactInformation;
